/**
 * Billing Settings Page
 *
 * Manage subscription and billing with redesigned pricing components.
 *
 * @module pages/settings/BillingPage
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks';
import { Card, CardHeader, Button, Alert, Spinner, Badge } from '@/components/ui';
import { CreditCard, FileText } from 'lucide-react';
import {
  PricingTableV2,
  FeatureComparisonV2,
  UpgradeConfirmModal,
} from '@/components/billing';
import {
  type PlanId,
  type BillingInterval,
  SLIDER_STOPS,
  PLAN_TIERS,
  formatRequestCount,
} from '@hummingbird/pricing';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

// =============================================================================
// Types
// =============================================================================

interface CheckoutResponse {
  url: string;
  sessionId: string;
}

interface PortalResponse {
  url: string;
}

// =============================================================================
// Component
// =============================================================================

export function BillingPage() {
  usePageTitle('Billing');

  const { organization } = useAuth();
  const currentPlanId = (organization?.planId || 'free') as PlanId;
  const currentBucketMax = organization?.currentBucketMax || 0;
  const hasSubscription = !!organization?.stripeSubscriptionId;

  // User is eligible for intro pricing if they've never had a subscription
  const isEligibleForIntro = !hasSubscription;

  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState<PlanId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Check for success message in URL params (after redirect)
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'upgraded') {
      setSuccessMessage('Your plan has been upgraded successfully.');
      // Clear the URL param
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
    } else if (success === 'downgraded') {
      setSuccessMessage('Your plan has been downgraded successfully. The new price will take effect at your next billing cycle.');
      // Clear the URL param
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Modal state for upgrade confirmation
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState<{
    planId: PlanId;
    requestCount: number;
    interval: BillingInterval;
  } | null>(null);

  // Shared state for slider and interval (synced between pricing table and comparison)
  // Default to customer's current billing interval if subscribed, otherwise monthly
  const getDefaultInterval = (): BillingInterval => {
    if (hasSubscription && organization?.billingInterval) {
      return organization.billingInterval;
    }
    return 'monthly';
  };
  const [interval, setInterval] = useState<BillingInterval>(getDefaultInterval);

  // Default slider to current bucket if subscribed, otherwise 10K
  const getDefaultSliderIndex = () => {
    if (hasSubscription && currentBucketMax > 0) {
      // Find the closest slider stop to the current bucket
      const index = SLIDER_STOPS.findIndex((s) => s.requests >= currentBucketMax);
      return index >= 0 ? index : SLIDER_STOPS.findIndex((s) => s.requests === 10000);
    }
    return SLIDER_STOPS.findIndex((s) => s.requests === 10000);
  };
  const [sliderIndex, setSliderIndex] = useState(getDefaultSliderIndex);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSelectPlan = async (
    planId: PlanId,
    requestCount: number,
    interval: BillingInterval
  ) => {
    if (!organization?.id) return;

    // Don't allow selecting exact same plan AND bucket (no change)
    // But DO allow changing buckets within the same plan
    const isSamePlanAndBucket = planId === currentPlanId &&
      Math.abs(requestCount - currentBucketMax) <= 1000;
    if (isSamePlanAndBucket) return;

    if (planId === 'free') {
      setError('To cancel your subscription, please use the billing portal.');
      return;
    }

    // Enterprise requires contact
    if (planId === 'enterprise') {
      window.location.href = 'mailto:sales@hummingbirdapi.com?subject=Enterprise%20Plan%20Inquiry';
      return;
    }

    // For existing subscribers, show confirmation modal
    if (hasSubscription) {
      setPendingUpgrade({ planId, requestCount, interval });
      setShowUpgradeModal(true);
      return;
    }

    // For new subscribers, go directly to checkout
    await handleNewSubscription(planId, requestCount, interval);
  };

  // Handle new subscription (checkout flow)
  const handleNewSubscription = async (
    planId: PlanId,
    requestCount: number,
    interval: BillingInterval
  ) => {
    if (!organization?.id) return;

    setIsLoading(true);
    setLoadingPlanId(planId);
    setError(null);

    try {
      const createCheckoutSession = httpsCallable<
        {
          planId: string;
          interval: string;
          requestCount: number;
          organizationId: string;
          successUrl: string;
          cancelUrl: string;
        },
        CheckoutResponse
      >(functions, 'createCheckoutSession');

      const result = await createCheckoutSession({
        planId,
        interval,
        requestCount,
        organizationId: organization.id,
        successUrl: `${window.location.origin}/settings/billing/success`,
        cancelUrl: `${window.location.origin}/settings/billing`,
      });

      // Redirect to Stripe Checkout
      window.location.href = result.data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to start checkout. Please try again.'
      );
      setIsLoading(false);
      setLoadingPlanId(null);
    }
  };

  // Handle confirmed upgrade (from modal)
  const handleConfirmUpgrade = async () => {
    if (!organization?.id || !pendingUpgrade) return;

    const { planId, requestCount, interval } = pendingUpgrade;

    setIsLoading(true);
    setLoadingPlanId(planId);
    setError(null);

    try {
      const updateSubscription = httpsCallable<
        {
          planId: string;
          interval: string;
          requestCount: number;
          organizationId: string;
        },
        { success: boolean; message: string; newBucketMax?: number }
      >(functions, 'updateSubscription');

      const result = await updateSubscription({
        planId,
        interval,
        requestCount,
        organizationId: organization.id,
      });

      if (result.data.success) {
        // Determine if this was an upgrade or downgrade
        const isDowngrade = requestCount < currentBucketMax;
        // Redirect with success param to show notification after reload
        window.location.href = `/settings/billing?success=${isDowngrade ? 'downgraded' : 'upgraded'}`;
      } else {
        setError(result.data.message || 'Failed to update subscription');
        setIsLoading(false);
        setLoadingPlanId(null);
        setShowUpgradeModal(false);
        setPendingUpgrade(null);
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to update subscription. Please try again.'
      );
      setIsLoading(false);
      setLoadingPlanId(null);
      setShowUpgradeModal(false);
      setPendingUpgrade(null);
    }
  };

  // Handle modal close
  const handleCloseUpgradeModal = () => {
    if (!isLoading) {
      setShowUpgradeModal(false);
      setPendingUpgrade(null);
    }
  };

  const handleManageBilling = async () => {
    if (!organization?.id || !hasSubscription) return;

    setIsLoading(true);
    setError(null);

    try {
      const createPortalSession = httpsCallable<
        { organizationId: string; returnUrl: string },
        PortalResponse
      >(functions, 'createPortalSession');

      const result = await createPortalSession({
        organizationId: organization.id,
        returnUrl: window.location.href,
      });

      // Redirect to Stripe Customer Portal
      window.location.href = result.data.url;
    } catch (err) {
      console.error('Portal error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to open billing portal. Please try again.'
      );
      setIsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!organization) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Success Alert */}
      {successMessage && (
        <Alert variant="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current Plan */}
      <Card>
        <CardHeader title="Current Plan" className="mb-2" />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Badge variant="primary" className="text-sm px-3 py-1">
              {PLAN_TIERS[currentPlanId]?.name || 'Free'}
            </Badge>
            {currentBucketMax > 0 ? (
              <span className="text-gray-600">
                {formatRequestCount(currentBucketMax)} requests/month
              </span>
            ) : (
              <span className="text-gray-500">2,000 requests lifetime</span>
            )}
          </div>
          {hasSubscription && (
            <div className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<CreditCard className="h-4 w-4" />}
                onClick={handleManageBilling}
                disabled={isLoading}
              >
                Update Payment Method
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<FileText className="h-4 w-4" />}
                onClick={handleManageBilling}
                disabled={isLoading}
              >
                View Invoices
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Pricing Table */}
      <div id="pricing-table">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
          Need more requests or features today?
          </h2>
          <p className="text-gray-500 mt-2 max-w-2xl mx-auto">
          Higher request limits, advanced add-ons, and priority support — all on the same API you're already using.
          </p>
        </div>

        <PricingTableV2
          currentPlanId={currentPlanId}
          currentBucketMax={currentBucketMax}
          isEligibleForIntro={isEligibleForIntro}
          onSelectPlan={handleSelectPlan}
          isLoading={isLoading}
          loadingPlanId={loadingPlanId ?? undefined}
          interval={interval}
          onIntervalChange={setInterval}
          sliderIndex={sliderIndex}
          onSliderIndexChange={setSliderIndex}
        />
      </div>

      {/* Feature Comparison */}
      <FeatureComparisonV2
        currentPlanId={currentPlanId}
        currentBucketMax={currentBucketMax}
        isEligibleForIntro={isEligibleForIntro}
        onSelectPlan={handleSelectPlan}
        isLoading={isLoading}
        loadingPlanId={loadingPlanId ?? undefined}
        interval={interval}
        onIntervalChange={setInterval}
        sliderIndex={sliderIndex}
        onSliderIndexChange={setSliderIndex}
      />

      {/* Intro Pricing Note */}
      {isEligibleForIntro && (
        <p className="text-xs text-gray-400 text-center">
          * Intro pricing (20% off) applies to your first billing period only.
        </p>
      )}

      {/* Upgrade Confirmation Modal */}
      {pendingUpgrade && (
        <UpgradeConfirmModal
          open={showUpgradeModal}
          onClose={handleCloseUpgradeModal}
          onConfirm={handleConfirmUpgrade}
          isLoading={isLoading}
          currentPlanId={currentPlanId}
          currentBucketMax={currentBucketMax}
          newPlanId={pendingUpgrade.planId}
          newBucketMax={pendingUpgrade.requestCount}
          interval={pendingUpgrade.interval}
        />
      )}
    </div>
  );
}
